import { create, StoreApi, UseBoundStore } from "zustand";
import { persist } from "zustand/middleware";
import { CollectionItem } from "../../types/collection";
import React, { type MouseEvent } from "react";
import type { GalleryImageType } from "../../hooks/events/useSingleImageDownload";

// Re-export for convenience
export type { CollectionItem };

type NullableString = string | null;
type NullableNumber = number | null;

const initialState = {
  // Gallery params
  requestId: null as NullableString,
  requestKey: null as NullableString,
  reelId: null as NullableString,
  accessKey: null as NullableString,
  orgId: null as NullableString,
  queryCollectionId: null as NullableString,
  queryCollectionKey: null as NullableString,
  albumSelection: false,
  filterText: undefined as string | undefined,
  teamView: false,
  allowPeopleTab: false,
  showLikeButton: false,

  // UI state
  isFaceSearchOpen: false,
  isGuestUploadOpen: false,
  isMyUploadsOpen: false,

  // Login state - email is persisted to localStorage via persist middleware
  openLogin: false,
  email: "",
  emailField: "",
  loginError: { email: "" } as { email: string },

  // Event key and type
  eventKey: null as NullableString,
  eventKeyType: null as number,

  // Access control state
  pin: null as NullableString,
  pinRequired: true,
  pinValidated: false,
  pinAccessLevel: null as NullableNumber,
  galleryPrivacy: null as NullableNumber,
  isValidating: true,

  // Collection data
  collectionData: [] as CollectionItem[],
  activeCollection: -1 as number,
  isCollectionLoading: false,
  showAllImages: false,

  // Tracking
  handleTrigger: null as ((triggerCategory: string, props?: Record<string, unknown>) => Promise<unknown> | void) | null,
  handleActivity: null as ((type: number, image_id: string, sponsor_id?: string | null) => Promise<void>) | null,

  // Single image download handler
  handleOnImageDownload: null as ((e: MouseEvent<HTMLElement>, imgData: GalleryImageType) => Promise<void>) | null,

  // Single image share handler
  handleOnImageShare: null as ((e: MouseEvent<HTMLElement>, imgData: GalleryImageType & { src?: string }) => Promise<void>) | null,

  // Download state tracking
  downloadStats: {
    downloaded: 0,
    downloadedVideos: 0,
    inDownloading: 0,
    failed: 0,
    downloadingImageIds: [] as number[],
  },

  // Navigator share lock (only one share at a time)
  isNavigatorShareInProgress: false,

  // Image sharing state (only one share at a time)
  isSharing: false,

  // Gallery navbar handlers
  handleOnOpenCart: null as (() => void) | null,
  handleOnDownload: null as (({ email }: { email: string }) => void) | null,
  startDownload: false,
  handleChangeShowAll: null as (() => void) | null,
  handlePWAInstall: null as (() => Promise<void>) | null,
  handleChangeAlbumSelection: null as ((value: boolean) => void) | null,
  setShowInquiryModal: null as ((show: boolean) => void) | null,
  handleOnLogout: null as (() => void) | null,
  handleOnOpenTransactionDetails: null as ((eventId: string | number) => void) | null,
  setIsOpenLangauageSelector: null as ((isOpen: boolean) => void) | null,
  liked: [] as number[],

  // Pre-registration data - persisted per event (map: eventId -> registrations array)
  registrations: {} as Record<string, { requestId: string; requestKey: string; selfieUrl: string; formData?: Record<string, string> }[]>,
};

export type GalleryParamsState = typeof initialState;

export interface GalleryParamsActions {
  setGalleryParams: (params: Partial<GalleryParamsState>) => void;
  setAlbumSelection: (albumSelection: boolean) => void;
  setCollectionData: (collectionData: CollectionItem[]) => void;
  setActiveCollection: (activeCollection: number) => void;
  setCollectionState: (state: {
    collectionData?: CollectionItem[];
    activeCollection?: number;
    isCollectionLoading?: boolean;
  }) => void;
  resetGalleryParams: () => void;
  setFaceSearchOpen: (isOpen: boolean) => void;
  setGuestUploadOpen: (isOpen: boolean) => void;
  setMyUploadsOpen: (isOpen: boolean) => void;
  toggleFaceSearch: () => void;
  toggleGuestUpload: () => void;
  toggleMyUploads: () => void;
  openFaceSearch: () => void;
  closeFaceSearch: () => void;
  openGuestUpload: () => void;
  closeGuestUpload: () => void;
  openMyUploads: () => void;
  closeMyUploads: () => void;
  setShowAllImages: (showAllImages: boolean) => void;
  toggleShowAllImages: () => void;
  handleTrigger: ((triggerCategory: string, props?: Record<string, unknown>) => Promise<unknown> | void) | null;
  setHandleTrigger: (handleTrigger: ((triggerCategory: string, props?: Record<string, unknown>) => Promise<unknown> | void) | null) => void;
  handleActivity: ((type: number, image_id: string, sponsor_id: string | null) => Promise<void>) | null;
  setHandleActivity: (handleActivity: ((type: number, image_id: string, sponsor_id: string | null) => Promise<void>) | null) => void;
  handleOnImageDownload: ((e: MouseEvent<HTMLElement>, imgData: GalleryImageType) => Promise<void>) | null;
  setHandleOnImageDownload: (handler: ((e: MouseEvent<HTMLElement>, imgData: GalleryImageType) => Promise<void>) | null) => void;
  handleOnImageShare: ((e: MouseEvent<HTMLElement>, imgData: GalleryImageType & { src?: string }) => Promise<void>) | null;
  setHandleOnImageShare: (handler: ((e: MouseEvent<HTMLElement>, imgData: GalleryImageType & { src?: string }) => Promise<void>) | null) => void;
  downloadStats: { downloaded: number; downloadedVideos: number; inDownloading: number; failed: number; downloadingImageIds: number[] };
  incrementDownloading: (imageId?: number) => void;
  incrementDownloaded: (imageId?: number) => void;
  incrementDownloadedVideo: () => void;
  incrementFailed: (imageId?: number) => void;
  resetDownloadStats: () => void;
  isImageDownloading: (imageId: number) => boolean;
  isNavigatorShareInProgress: boolean;
  setNavigatorShareInProgress: (inProgress: boolean) => void;
  isSharing: boolean;
  setIsSharing: (isSharing: boolean) => void;
  // Gallery navbar handlers
  setHandleOnOpenCart: (handler: (() => void) | null) => void;
  setHandleOnDownload: (handler: (({ email }: { email: string }) => void) | null) => void;
  setStartDownload: (startDownload: boolean) => void;
  setHandleChangeShowAll: (handler: (() => void) | null) => void;
  setHandlePWAInstall: (handler: (() => Promise<void>) | null) => void;
  setHandleChangeAlbumSelection: (handler: ((value: boolean) => void) | null) => void;
  setSetShowInquiryModal: (handler: ((show: boolean) => void) | null) => void;
  setHandleOnLogout: (handler: (() => void) | null) => void;
  setHandleOnOpenTransactionDetails: (handler: ((eventId: string | number) => void) | null) => void;
  setSetIsOpenLangauageSelector: (handler: ((isOpen: boolean) => void) | null) => void;
  setLiked: (liked: number[]) => void;
  // Login actions
  setOpenLogin: (openLogin: boolean) => void;
  setEmail: (email: string) => void;
  setEmailField: (emailField: string) => void;
  setLoginError: (loginError: { email: string }) => void;
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLogin: () => void;
  checkLogin: () => boolean | void;
  // Registration actions
  addRegistration: (eventId: string, requestId: string, requestKey: string, selfieUrl: string, formData?: Record<string, string>) => void;
  getRegistrationsForEvent: (eventId: string) => { requestId: string; requestKey: string; selfieUrl: string; formData?: Record<string, string> }[];
  hasRegistrationForEvent: (eventId: string) => boolean;
}

// Auto-generate selectors utility (from Zustand docs)
type WithSelectors<S> = S extends { getState: () => infer T }
  ? S & { use: { [K in keyof T]: () => T[K] } }
  : never;

const createSelectors = <S extends UseBoundStore<StoreApi<object>>>(
  _store: S,
) => {
  const store = _store as WithSelectors<typeof _store>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  store.use = {} as any;
  for (const k of Object.keys(store.getState())) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (store.use as any)[k] = () => store((s) => s[k as keyof typeof s]);
  }
  return store;
};

// Create base store with persist middleware (only email is persisted)
const useGalleryStoreBase = create<
  GalleryParamsState & GalleryParamsActions
>()(
  persist(
    (set, get) => ({
      ...initialState,
      setGalleryParams: (params) =>
        set((state) => ({
          ...state,
          ...params,
        })),
      setAlbumSelection: (albumSelection) => set({ albumSelection }),
      setCollectionData: (collectionData) => set({ collectionData }),
      setActiveCollection: (activeCollection) => set({ activeCollection }),
      setCollectionState: (state) =>
        set((prevState) => ({
          ...prevState,
          ...state,
        })),
      resetGalleryParams: () => set(() => ({ ...initialState })),
      setFaceSearchOpen: (isFaceSearchOpen) => set({ isFaceSearchOpen }),
      setGuestUploadOpen: (isGuestUploadOpen) => set({ isGuestUploadOpen }),
      setMyUploadsOpen: (isMyUploadsOpen) => set({ isMyUploadsOpen }),
      toggleFaceSearch: () =>
        set((prevState) => ({
          isFaceSearchOpen: !prevState.isFaceSearchOpen,
        })),
      toggleGuestUpload: () =>
        set((prevState) => ({
          isGuestUploadOpen: !prevState.isGuestUploadOpen,
        })),
      toggleMyUploads: () =>
        set((prevState) => ({
          isMyUploadsOpen: !prevState.isMyUploadsOpen,
        })),
      openFaceSearch: () => set({ isFaceSearchOpen: true }),
      closeFaceSearch: () => set({ isFaceSearchOpen: false }),
      openGuestUpload: () => set({ isGuestUploadOpen: true }),
      closeGuestUpload: () => set({ isGuestUploadOpen: false }),
      openMyUploads: () => set({ isMyUploadsOpen: true }),
      closeMyUploads: () => set({ isMyUploadsOpen: false }),
      setShowAllImages: (showAllImages) => set({ showAllImages }),
      toggleShowAllImages: () =>
        set((prevState) => ({
          showAllImages: !prevState.showAllImages,
        })),
      setHandleTrigger: (handleTrigger) => set({ handleTrigger }),
      setHandleActivity: (handleActivity) => set({ handleActivity }),
      setHandleOnImageDownload: (handleOnImageDownload) => set({ handleOnImageDownload }),
      setHandleOnImageShare: (handleOnImageShare) => set({ handleOnImageShare }),
      incrementDownloading: (imageId?: number) =>
        set((state) => {
          const newDownloadingIds = [...state.downloadStats.downloadingImageIds];
          if (imageId !== undefined && !newDownloadingIds.includes(imageId)) {
            newDownloadingIds.push(imageId);
          }
          return {
            downloadStats: {
              ...state.downloadStats,
              inDownloading: state.downloadStats.inDownloading + 1,
              downloadingImageIds: newDownloadingIds,
            },
          };
        }),
      incrementDownloaded: (imageId?: number) =>
        set((state) => {
          const newDownloadingIds = state.downloadStats.downloadingImageIds.filter(
            (id) => id !== imageId
          );
          return {
            downloadStats: {
              ...state.downloadStats,
              downloaded: state.downloadStats.downloaded + 1,
              inDownloading: Math.max(0, state.downloadStats.inDownloading - 1),
              downloadingImageIds: newDownloadingIds,
            },
          };
        }),
      incrementDownloadedVideo: () =>
        set((state) => ({
          downloadStats: {
            ...state.downloadStats,
            downloadedVideos: state.downloadStats.downloadedVideos + 1,
            inDownloading: Math.max(0, state.downloadStats.inDownloading - 1),
          },
        })),
      incrementFailed: (imageId?: number) =>
        set((state) => {
          const newDownloadingIds = state.downloadStats.downloadingImageIds.filter(
            (id) => id !== imageId
          );
          return {
            downloadStats: {
              ...state.downloadStats,
              failed: state.downloadStats.failed + 1,
              inDownloading: Math.max(0, state.downloadStats.inDownloading - 1),
              downloadingImageIds: newDownloadingIds,
            },
          };
        }),
      resetDownloadStats: () =>
        set({
          downloadStats: {
            downloaded: 0,
            downloadedVideos: 0,
            inDownloading: 0,
            failed: 0,
            downloadingImageIds: [],
          },
        }),
      isImageDownloading: (imageId: number) => {
        const state = get();
        return state.downloadStats.downloadingImageIds.includes(imageId);
      },
      setNavigatorShareInProgress: (isNavigatorShareInProgress) =>
        set({ isNavigatorShareInProgress }),
      setIsSharing: (isSharing) => set({ isSharing }),
      // Gallery navbar handlers
      setHandleOnOpenCart: (handleOnOpenCart) => set({ handleOnOpenCart }),
      setHandleOnDownload: (handleOnDownload) => set({ handleOnDownload }),
      setStartDownload: (startDownload) => set({ startDownload }),
      setHandleChangeShowAll: (handleChangeShowAll) => set({ handleChangeShowAll }),
      setHandlePWAInstall: (handlePWAInstall) => set({ handlePWAInstall }),
      setHandleChangeAlbumSelection: (handleChangeAlbumSelection) => set({ handleChangeAlbumSelection }),
      setSetShowInquiryModal: (setShowInquiryModal) => set({ setShowInquiryModal }),
      setHandleOnLogout: (handleOnLogout) => set({ handleOnLogout }),
      setHandleOnOpenTransactionDetails: (handleOnOpenTransactionDetails) => set({ handleOnOpenTransactionDetails }),
      setSetIsOpenLangauageSelector: (setIsOpenLangauageSelector) => set({ setIsOpenLangauageSelector }),
      setLiked: (liked) => set({ liked }),
      setOpenLogin: (openLogin) => set({ openLogin }),
      setEmail: (email) => set({ email }), // Persist middleware handles localStorage
      setEmailField: (emailField) => set({ emailField }),
      setLoginError: (loginError) => set({ loginError }),
      onEmailChange: (e) => set({ emailField: e.target.value }),
      onLogin: () => {
        const state = get();
        const validateEmail = (email: string) => {
          return String(email)
            .toLowerCase()
            .match(
              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            );
        };
        if (!validateEmail(state.emailField)) {
          set({ loginError: { email: "Invalid Email" } });
        } else {
          set({ loginError: { email: "" } });
          // Persist middleware will automatically save email to localStorage
          set({ email: state.emailField, openLogin: false });
        }
      },
      checkLogin: () => {
        const state = get();
        if (!state.email || state.email === "") {
          set({ openLogin: true });
        } else return true;
      },
      addRegistration: (eventId, requestId, requestKey, selfieUrl, formData) => {
        set((state) => ({
          registrations: {
            ...state.registrations,
            [eventId]: [...(state.registrations[eventId] || []), { requestId, requestKey, selfieUrl, formData }],
          },
        }));
      },
      getRegistrationsForEvent: (eventId) => {
        const state = get();
        return state.registrations[eventId] || [];
      },
      hasRegistrationForEvent: (eventId) => {
        const state = get();
        return (state.registrations[eventId]?.length || 0) > 0;
      },
    }),
    {
      name: "gallery-storage", // localStorage key
      // Persist email and registrations
      partialize: (state) => ({ email: state.email, registrations: state.registrations }),
      // Custom storage adapter to handle QuotaExceededError gracefully
      storage: {
        getItem: (name: string) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          try {
            return JSON.parse(str);
          } catch {
            return null;
          }
        },
        setItem: (name: string, value: unknown) => {
          try {
            localStorage.setItem(name, JSON.stringify(value));
          } catch (e) {
            if (e instanceof DOMException && (e.name === 'QuotaExceededError' || e.code === 22)) {
              // Storage quota exceeded — try to clean up and retry
              // console.warn('[gallery-storage] localStorage quota exceeded, attempting cleanup...');
              try {
                // Safely deep clone the value so we don't mutate the live Zustand in-memory store
                const parsed = JSON.parse(JSON.stringify(value));
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const state = (parsed as any)?.state;
                if (state?.registrations) {
                  const regs = state.registrations;
                  for (const eventId of Object.keys(regs)) {
                    if (Array.isArray(regs[eventId])) {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      regs[eventId] = regs[eventId].map((reg: any) => ({
                        ...reg,
                        // Strip base64 selfie data to save space, keep URL-based selfies
                        selfieUrl: reg.selfieUrl && reg.selfieUrl.startsWith('data:') ? '' : reg.selfieUrl,
                      }));
                    }
                  }
                }
                localStorage.setItem(name, JSON.stringify(parsed));
              } catch {
                // If cleanup also fails, silently ignore — the app will still work, just without persisted registrations for this session
                // console.warn('[gallery-storage] Could not persist state to localStorage. Storage is full.');
                console.error(e);
              }
            } else {
              console.error(e);
            }
          }
        },
        removeItem: (name: string) => {
          localStorage.removeItem(name);
        },
      },
    }
  )
);

// Apply auto-generated selectors to base store
export const useGalleryStore = createSelectors(useGalleryStoreBase);

// Convenience hook for login-related states and actions using auto-generated selectors
export const useLoginStore = () => {
  return {
    // States
    openLogin: useGalleryStore.use.openLogin(),
    email: useGalleryStore.use.email(),
    emailField: useGalleryStore.use.emailField(),
    loginError: useGalleryStore.use.loginError(),
    // Actions
    setOpenLogin: useGalleryStore.use.setOpenLogin(),
    setEmail: useGalleryStore.use.setEmail(),
    setEmailField: useGalleryStore.use.setEmailField(),
    setLoginError: useGalleryStore.use.setLoginError(),
    onEmailChange: useGalleryStore.use.onEmailChange(),
    onLogin: useGalleryStore.use.onLogin(),
    checkLogin: useGalleryStore.use.checkLogin(),
  };
};

// Convenience hook for single image download using auto-generated selectors
export const useSingleImageDownloadStore = () => {
  return {
    handleOnImageDownload: useGalleryStore.use.handleOnImageDownload(),
    setHandleOnImageDownload: useGalleryStore.use.setHandleOnImageDownload(),
    handleOnImageShare: useGalleryStore.use.handleOnImageShare(),
    setHandleOnImageShare: useGalleryStore.use.setHandleOnImageShare(),
    downloadStats: useGalleryStore.use.downloadStats(),
    incrementDownloading: useGalleryStore.use.incrementDownloading(),
    incrementDownloaded: useGalleryStore.use.incrementDownloaded(),
    incrementFailed: useGalleryStore.use.incrementFailed(),
    resetDownloadStats: useGalleryStore.use.resetDownloadStats(),
    isImageDownloading: useGalleryStore.use.isImageDownloading(),
    isSharing: useGalleryStore.use.isSharing(),
    setIsSharing: useGalleryStore.use.setIsSharing(),
  };
};
